import React, { useCallback, useState } from 'react';
import type { IntlShape } from 'react-intl';
import { useKeyPress } from '@/components/EventHero/HeroCarousel/useKeyPress';
import { useMediaQuery } from '@/hooks';
import type { AffiliateCodes } from '@/components/_shared/constants';
import type { OrganizerEvent } from '../../../types';
import { MoreOrganizerEventsRedesignCard } from '../MoreOrganizerEventsRedesignCard';
import { useCarousel } from '../../hooks/useCarousel';
import { CarouselControls } from '../CarouselControls';
import styles from './MoreOrganizerEventsCarousel.module.scss';

type MoreOrganizerEventsCarouselProps = {
  affiliateCode?: AffiliateCodes;
  eventsToShow: OrganizerEvent[];
  formatMessage: IntlShape['formatMessage'];
};

export const MoreOrganizerEventsCarousel = ({
  affiliateCode,
  eventsToShow,
  formatMessage,
}: MoreOrganizerEventsCarouselProps) => {
  const isTabletOrDesktop = useMediaQuery('(min-width: 744px)');
  const [isRailFocused, setIsRailFocused] = useState(false);
  const { canScrollNext, canScrollPrev, hasOverflow, setCardRef, showNext, showPrev, syncCarouselState, viewportRef } =
    useCarousel({ itemCount: eventsToShow.length });

  const shouldShowArrows = isTabletOrDesktop && hasOverflow;

  const handleBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsRailFocused(false);
    }
  }, []);

  useKeyPress({
    isCarouselFocused: isRailFocused,
    onLeftKeyPressed: showPrev,
    onRightKeyPressed: showNext,
  });

  const controls = shouldShowArrows ? (
    <CarouselControls
      canScrollNext={canScrollNext}
      canScrollPrev={canScrollPrev}
      formatMessage={formatMessage}
      onNext={showNext}
      onPrev={showPrev}
    />
  ) : null;

  const organizerEventCards = eventsToShow.map((organizerEvent, index) => (
    <div
      className={styles.cardSlot}
      key={organizerEvent.id || `organizer-event-${index}`}
      ref={(node) => {
        setCardRef(index, node);
      }}
    >
      <MoreOrganizerEventsRedesignCard affiliateCode={affiliateCode} organizerEvent={organizerEvent} />
    </div>
  ));

  const viewportTabIndex = shouldShowArrows ? 0 : -1;

  return (
    <>
      <div className={styles.sectionHeader}>
        <h3 className={styles.title}>
          {formatMessage({
            id: 'From this organizer',
          })}
        </h3>
        {controls}
      </div>
      <div
        className={styles.viewport}
        onBlur={handleBlur}
        onFocus={() => setIsRailFocused(true)}
        onScroll={syncCarouselState}
        ref={viewportRef}
        tabIndex={viewportTabIndex}
      >
        {organizerEventCards}
      </div>
    </>
  );
};
